import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, BadgeCheck, Lock, Search, PackageCheck, Sparkles, ShieldAlert } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { errMsg, api } from "@/lib/api";

const STEPS = [
  [Search, "Finden", "PLZ eingeben und Waschhelden in deiner Nähe vergleichen. Die Preise macht Washly – überall gleich und fair."],
  [PackageCheck, "Übergeben", "Wäsche per QR-Code oder PIN übergeben. Dokumentiert und nachvollziehbar – bezahlt wird erst nach Zusage."],
  [Sparkles, "Zurücklehnen", "Gewaschen, getrocknet, gefaltet – frisch zurück vor deiner Tür. Der Rest des Tages gehört dir."],
];

function AuthSocialProof() {
  return (
    <section className="w-container pb-14 pt-4" data-testid="auth-social-proof">
      <div className="rounded-[28px] border border-line bg-white p-6 md:p-8">
        <div className="max-w-xl mb-8">
          <p className="text-sm font-bold text-mint-ink uppercase tracking-wider mb-1">Frisch gestartet in deiner Nachbarschaft</p>
          <h2 className="text-2xl md:text-3xl font-extrabold">In drei Schritten zu freier Zeit.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          {STEPS.map(([Icon, title, text], i) => (
            <div key={title} className="w-card p-5 w-hover-lift" data-testid="auth-step">
              <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-mint-soft text-mint-ink mb-3"><Icon size={24} aria-hidden="true" /></div>
              <h3 className="font-extrabold mb-1"><span className="text-mint-ink">{i + 1}.</span> {title}</h3>
              <p className="w-muted text-sm">{text}</p>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <span className="w-chip bg-mint-soft text-mint-ink"><ShieldCheck size={16} aria-hidden="true" /> Geprüfte Versicherungsnachweise</span>
          <span className="w-chip bg-mint-soft text-mint-ink"><BadgeCheck size={16} aria-hidden="true" /> Identitätsgeprüfte Waschhelden</span>
          <span className="w-chip bg-mint-soft text-mint-ink"><Lock size={16} aria-hidden="true" /> Zahlung erst nach Zusage</span>
        </div>
        <p className="w-muted text-sm mt-6">Schließ dich an – als Kunde oder als Waschheld.</p>
      </div>
    </section>
  );
}

export default function AuthPage() {
  const [params] = useSearchParams();
  const next = params.get("next") || "";
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [form, setForm] = useState({ email: "", password: "", name: "", code: "", role: params.get("role") === "provider" ? "provider" : "customer", accept_risk: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const { login, register, requestCode, verifyCode } = useAuth();
  const { t, lang } = useT();
  const nav = useNavigate();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const title = mode === "login" ? t("auth.login") : mode === "register" ? t("auth.register") : mode === "code" ? "Mit E-Mail-Code anmelden" : t("auth.forgot_title");

  const afterAuth = (u) => {
    toast.success(`Willkommen, ${u.name}`);
    if (next) nav(next);
    else if (u.role === "provider" || u.has_provider) nav("/provider");
    else if (["admin", "support", "finance"].includes(u.role)) nav("/backoffice");
    else nav("/search");
  };

  if (mode === "forgot" && sent) {
    return (
      <div className="w-container py-10 max-w-lg">
        <div className="w-card p-6 md:p-8 w-rise" data-testid="forgot-sent">
          <h1 className="text-3xl font-extrabold mb-3">{t("auth.forgot_sent_title")}</h1>
          <p className="w-muted mb-6">{sent}</p>
          <button type="button" className="w-btn w-btn-primary w-full" onClick={() => { setMode("login"); setSent(""); }} data-testid="forgot-back-login">{t("auth.back_login")}</button>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email: form.email });
        setSent(data.message);
        return;
      }
      if (mode === "code") {
        if (!codeSent) {
          await requestCode(form.email.trim());
          setCodeSent(true);
          toast.success("Falls ein Konto existiert, haben wir dir einen Code gesendet.");
          return;
        }
        const u = await verifyCode(form.email.trim(), form.code.trim());
        afterAuth(u);
        return;
      }
      const u = mode === "login" ? await login(form.email, form.password) : await register({ ...form, language: lang });
      afterAuth(u);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
    <div className="w-container pt-10 pb-4 grid md:grid-cols-2 gap-10 items-start">
      <div className="hidden md:flex flex-col pt-2">
        <div className="relative overflow-hidden rounded-[28px] bg-mint text-mint-on p-8 lg:p-10 w-rise" data-testid="auth-brand-panel">
          <div className="w-blob w-blob-a" aria-hidden="true" />
          <div className="w-blob w-blob-violet" style={{ bottom: "-120px", left: "-80px" }} aria-hidden="true" />
          <div className="relative">
            <span className="inline-grid place-items-center h-16 rounded-2xl bg-white px-4 mb-6 shadow"><img src="/logo.png" alt="" className="h-9 w-auto" /></span>
            <h1 className="text-4xl font-extrabold mb-4">{title}</h1>
            <p className="text-mint-on/80 text-lg">{mode === "forgot" ? t("auth.forgot_desc") : next ? t("book.login_needed") : "Preise und Profile siehst du auch ohne Konto. Für Buchungen brauchst du eins."}</p>
            <ul className="mt-7 space-y-3 font-semibold">
              <li className="flex items-center gap-3"><span className="h-9 w-9 rounded-full bg-mint-on text-mint grid place-items-center shrink-0"><ShieldCheck size={18} aria-hidden="true" /></span> {t("trust.insured")}</li>
              <li className="flex items-center gap-3"><span className="h-9 w-9 rounded-full bg-mint-on text-mint grid place-items-center shrink-0"><BadgeCheck size={18} aria-hidden="true" /></span> {t("trust.kyc")}</li>
              <li className="flex items-center gap-3"><span className="h-9 w-9 rounded-full bg-mint-on text-mint grid place-items-center shrink-0"><Lock size={18} aria-hidden="true" /></span> {t("trust.escrow")}</li>
            </ul>
          </div>
        </div>
      </div>
      <form onSubmit={submit} className="w-card p-6 md:p-8 space-y-4 w-rise" data-testid="auth-form">
        <div className="md:hidden flex items-center gap-2 mb-1">
          <img src="/logo.png" alt="" className="h-7 w-auto" />
          <span className="font-extrabold text-xl">Washly</span>
        </div>
        <h1 className="md:hidden text-3xl font-extrabold">{title}</h1>
        {mode === "forgot" && <p className="md:hidden w-muted">{t("auth.forgot_desc")}</p>}
        {next && <p className="md:hidden w-muted">{t("book.login_needed")}</p>}
        {mode === "register" && (
          <>
            <label className="block"><span className="w-label">{t("auth.name")}</span><input className="w-input" required minLength={2} value={form.name} onChange={set("name")} autoComplete="name" data-testid="auth-name-input" /></label>
            <fieldset>
              <legend className="w-label">{t("auth.role")}</legend>
              <div className="grid grid-cols-2 gap-2">
                {["customer", "provider"].map((r) => (
                  <label key={r} className={`w-card p-3 cursor-pointer flex items-center gap-2 ${form.role === r ? "!border-[var(--w-mint-ink)] bg-mint-soft" : ""}`}>
                    <input type="radio" name="role" value={r} checked={form.role === r} onChange={set("role")} data-testid={`auth-role-${r}`} /> {t(`auth.role_${r}`)}
                  </label>
                ))}
              </div>
            </fieldset>
            {form.role === "provider" && (
              <label className="flex items-start gap-3 text-sm cursor-pointer rounded-2xl border border-amber-300 bg-amber-50 p-4" data-testid="auth-provider-risk-label">
                <input type="checkbox" className="h-5 w-5 mt-0.5 accent-[var(--w-mint-ink)]" checked={form.accept_risk} onChange={(e) => setForm({ ...form, accept_risk: e.target.checked })} data-testid="auth-accept-risk" />
                <span className="leading-relaxed"><span className="flex items-center gap-1.5 font-bold text-amber-900 mb-1"><ShieldAlert size={16} aria-hidden="true" /> Eigenverantwortung &amp; Pflichten</span>Ich bestätige, dass ich meine Tätigkeit auf eigene Verantwortung ausübe. Ich bin selbst dafür verantwortlich, meine steuerlichen, gewerblichen, sozialversicherungsrechtlichen und sonstigen gesetzlichen Pflichten zu prüfen und einzuhalten. Mir ist bekannt, dass Washly keine individuelle Steuer- oder Rechtsberatung erbringt und meine persönliche Haftung nicht übernimmt.</span>
              </label>
            )}
          </>
        )}
        <label className="block"><span className="w-label">{t("auth.email")}</span><input className="w-input" type="email" required value={form.email} onChange={set("email")} autoComplete="email" data-testid="auth-email-input" /></label>
        {(mode === "login" || mode === "register") && (
        <label className="block"><span className="w-label">{t("auth.password")}</span><input className="w-input" type="password" required minLength={mode === "register" ? 8 : 1} value={form.password} onChange={set("password")} autoComplete={mode === "login" ? "current-password" : "new-password"} data-testid="auth-password-input" />
          {mode === "register" && <span className="w-muted text-sm">{t("auth.pw_hint")}</span>}</label>
        )}
        {mode === "code" && codeSent && (
          <label className="block"><span className="w-label">6-stelliger Code aus der E-Mail</span>
            <div className="flex gap-2">
              <input className="w-input tracking-[0.4em] font-mono text-center text-xl flex-1" inputMode="numeric" required minLength={6} maxLength={6} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.replace(/\D/g, "") })} autoComplete="one-time-code" placeholder="••••••" data-testid="auth-code-input" />
              <button type="button" className="w-btn w-btn-secondary !px-3" title="Code aus Zwischenablage einfügen" data-testid="auth-code-paste"
                onClick={async () => { try { const txt = await navigator.clipboard.readText(); const d = (txt.match(/\d/g) || []).join("").slice(0, 6); if (d) setForm({ ...form, code: d }); else toast.error("Kein Code in der Zwischenablage"); } catch { toast.error("Zwischenablage nicht verfügbar"); } }}>Einfügen</button>
            </div>
            <button type="button" className="w-muted text-sm underline mt-1" onClick={() => { setCodeSent(false); setForm({ ...form, code: "" }); }} data-testid="auth-code-resend">Code erneut anfordern</button></label>
        )}
        {mode === "code" && !codeSent && <p className="w-muted text-sm -mt-1">Wir senden dir einen Einmal-Code an deine E-Mail – kein Passwort nötig.</p>}
        {mode === "login" && (
          <p className="text-right -mt-2"><button type="button" className="text-sm font-semibold underline text-mint-ink" onClick={() => { setMode("forgot"); setError(""); }} data-testid="auth-forgot-link">{t("auth.forgot")}</button></p>
        )}
        {error && <p role="alert" className="font-semibold text-[var(--w-error)]" data-testid="auth-error">{error}</p>}
        <button className="w-btn w-btn-primary w-full" disabled={busy || (mode === "register" && form.role === "provider" && !form.accept_risk)} data-testid="auth-submit-btn">{busy ? t("common.loading") : mode === "forgot" ? t("auth.forgot_submit") : mode === "code" ? (codeSent ? "Anmelden" : "Code senden") : title}</button>
        {(mode === "login" || mode === "code") && (
          <button type="button" className="w-btn w-btn-secondary w-full !mt-2" onClick={() => { setMode(mode === "code" ? "login" : "code"); setError(""); setCodeSent(false); setForm({ ...form, code: "", password: "" }); }} data-testid="auth-toggle-code">
            {mode === "code" ? "Lieber mit Passwort anmelden" : "Ohne Passwort – Code per E-Mail"}
          </button>
        )}
        <p className="text-center">
          {mode === "forgot" ? (
            <button type="button" className="font-semibold underline text-mint-ink" onClick={() => setMode("login")} data-testid="auth-toggle-mode">{t("auth.back_login")}</button>
          ) : (<>
          {mode === "login" ? t("auth.no_account") : t("auth.have_account")}{" "}
          <button type="button" className="font-semibold underline text-mint-ink" onClick={() => setMode(mode === "login" ? "register" : "login")} data-testid="auth-toggle-mode">
            {mode === "login" ? t("auth.register") : t("auth.login")}
          </button>
          </>)}
        </p>
        <p className="text-center w-muted text-sm"><Link to="/search" className="underline">Weiter ohne Konto stöbern</Link></p>
      </form>
    </div>
    <AuthSocialProof />
    </>
  );
}
